import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useI18n } from '../context/I18nContext';
import { ArrowLeft, Gift, Users, Trophy, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';

const rules = {
  en: {
    title: 'Referral Program Rules',
    subtitle: 'Invite friends, earn rewards on all HexoPDF plans.',
    how_title: 'How it works',
    steps: [
      'Share your unique referral link from your account page.',
      'When someone clicks your link and purchases a HexoPDF license, you earn a referral point.',
      'Reach tier thresholds to unlock rewards automatically.',
    ],
    tiers_title: 'Reward Tiers',
    tiers: [
      { name: 'Bronze', min: 1, max: 4, reward: '5% discount on your next license purchase', icon: Gift, color: 'text-amber-600' },
      { name: 'Silver', min: 5, max: 14, reward: '10% discount on your next license purchase', icon: Star, color: 'text-zinc-400' },
      { name: 'Gold', min: 15, max: 49, reward: 'Free Standard license (1 device)', icon: Trophy, color: 'text-yellow-400' },
      { name: 'Diamond', min: 50, max: null, reward: 'Free Founder Edition (1 device)', icon: Trophy, color: 'text-cyan-400' },
    ],
    conditions_title: 'Conditions',
    conditions: [
      'Only completed purchases count toward referral points.',
      'Self-referrals are not allowed and will be invalidated.',
      'Rewards are applied automatically to your account.',
      'HexoPDF reserves the right to modify the program terms at any time.',
      'Discount rewards apply to your next purchase of any HexoPDF license.',
      'Each reward is valid for one license or single purchase.',
    ],
    back: 'Back to account',
  },
  fr: {
    title: 'Règles du programme de parrainage',
    subtitle: 'Invitez des amis, gagnez des réductions sur tous les plans HexoPDF.',
    how_title: 'Comment ça marche',
    steps: [
      'Partagez votre lien de parrainage unique depuis votre page de compte.',
      "Quand quelqu'un clique sur votre lien et achète une licence HexoPDF, vous gagnez un point de parrainage.",
      'Atteignez les seuils de palier pour débloquer des récompenses automatiquement.',
    ],
    tiers_title: 'Paliers de récompenses',
    tiers: [
      { name: 'Bronze', min: 1, max: 4, reward: '5% de réduction sur votre prochain achat de licence', icon: Gift, color: 'text-amber-600' },
      { name: 'Argent', min: 5, max: 14, reward: '10% de réduction sur votre prochain achat de licence', icon: Star, color: 'text-zinc-400' },
      { name: 'Or', min: 15, max: 49, reward: 'Licence Standard gratuite (1 appareil)', icon: Trophy, color: 'text-yellow-400' },
      { name: 'Diamant', min: 50, max: null, reward: 'Édition Fondateur gratuite (1 appareil)', icon: Trophy, color: 'text-cyan-400' },
    ],
    conditions_title: 'Conditions',
    conditions: [
      'Seuls les achats complétés comptent pour les points de parrainage.',
      'Les auto-parrainages ne sont pas autorisés et seront invalidés.',
      'Les récompenses sont appliquées automatiquement à votre compte.',
      'HexoPDF se réserve le droit de modifier les termes du programme à tout moment.',
      "Les réductions s'appliquent à votre prochain achat de n'importe quelle licence HexoPDF.",
      'Chaque récompense est valable pour une licence ou un achat unique.',
    ],
    back: 'Retour au compte',
  },
  hi: {
    title: 'रेफरल कार्यक्रम के नियम',
    subtitle: 'दोस्तों को आमंत्रित करें, सभी HexoPDF प्लान पर पुरस्कार अर्जित करें।',
    how_title: 'यह कैसे काम करता है',
    steps: [
      'अपने खाता पृष्ठ से अपना अद्वितीय रेफरल लिंक साझा करें।',
      'जब कोई आपके लिंक पर क्लिक करके HexoPDF लाइसेंस खरीदता है, तो आप एक रेफरल पॉइंट अर्जित करते हैं।',
      'पुरस्कार स्वचालित रूप से अनलॉक करने के लिए स्तर सीमाएं प्राप्त करें।',
    ],
    tiers_title: 'पुरस्कार स्तर',
    tiers: [
      { name: 'ब्रॉन्ज़', min: 1, max: 4, reward: 'अगली लाइसेंस खरीद पर 5% छूट', icon: Gift, color: 'text-amber-600' },
      { name: 'सिल्वर', min: 5, max: 14, reward: 'अगली लाइसेंस खरीद पर 10% छूट', icon: Star, color: 'text-zinc-400' },
      { name: 'गोल्ड', min: 15, max: 49, reward: 'मुफ़्त स्टैंडर्ड लाइसेंस (1 डिवाइस)', icon: Trophy, color: 'text-yellow-400' },
      { name: 'डायमंड', min: 50, max: null, reward: 'मुफ़्त फाउंडर एडिशन (1 डिवाइस)', icon: Trophy, color: 'text-cyan-400' },
    ],
    conditions_title: 'शर्तें',
    conditions: [
      'केवल पूर्ण खरीद रेफरल पॉइंट में गिनी जाती है।',
      'स्वयं-रेफरल की अनुमति नहीं है और अमान्य किए जाएंगे।',
      'पुरस्कार स्वचालित रूप से आपके खाते में लागू होते हैं।',
      'HexoPDF किसी भी समय कार्यक्रम की शर्तों को संशोधित करने का अधिकार सुरक्षित रखता है।',
      'छूट पुरस्कार किसी भी HexoPDF लाइसेंस की अगली खरीद पर लागू होते हैं।',
      'प्रत्येक पुरस्कार एक लाइसेंस या एकल खरीद के लिए मान्य है।',
    ],
    back: 'खाते पर वापस जाएं',
  },
};

export default function ReferralRulesPage() {
  const { lang } = useI18n();
  const l = rules[lang] || rules.en;

  return (
    <div className="min-h-screen bg-background" data-testid="referral-rules-page">
      <Navbar />
      <div className="max-w-3xl mx-auto px-4 py-16 sm:py-20">
        <Link to="/account" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground text-sm mb-8 transition-colors" data-testid="referral-rules-back">
          <ArrowLeft className="w-4 h-4" /> {l.back}
        </Link>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <h1 className="font-heading text-3xl font-bold text-foreground mb-2" data-testid="referral-rules-title">{l.title}</h1>
          <p className="text-muted-foreground text-sm mb-10">{l.subtitle}</p>

          {/* How it works */}
          <div className="mb-10">
            <h2 className="text-foreground font-semibold text-lg mb-4">{l.how_title}</h2>
            <div className="space-y-3">
              {l.steps.map((step, i) => (
                <div key={i} className="flex gap-3 items-start">
                  <span className="w-7 h-7 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-400 text-sm font-bold flex items-center justify-center flex-shrink-0">{i + 1}</span>
                  <p className="text-foreground/80 text-sm pt-1">{step}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Tiers */}
          <div className="mb-10">
            <h2 className="text-foreground font-semibold text-lg mb-4">{l.tiers_title}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {l.tiers.map((tier, i) => {
                const Icon = tier.icon;
                return (
                  <div key={i} className="p-4 rounded-xl bg-card border border-border">
                    <div className="flex items-center gap-2 mb-2">
                      <Icon className={`w-5 h-5 ${tier.color}`} />
                      <h3 className={`font-bold ${tier.color}`}>{tier.name}</h3>
                    </div>
                    <p className="text-muted-foreground text-xs mb-1">{tier.min}{tier.max ? `-${tier.max}` : '+'} parrainages</p>
                    <p className="text-foreground text-sm">{tier.reward}</p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Conditions */}
          <div className="mb-10">
            <h2 className="text-foreground font-semibold text-lg mb-4">{l.conditions_title}</h2>
            <ul className="space-y-2">
              {l.conditions.map((c, i) => (
                <li key={i} className="text-foreground/70 text-sm flex items-start gap-2">
                  <span className="text-muted-foreground">-</span>
                  {c}
                </li>
              ))}
            </ul>
          </div>
        </motion.div>
      </div>
      <Footer />
    </div>
  );
}
